import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';

export interface IWeekDetails {
  isCurrentWeek: boolean;
  isPastWeek: boolean;
  isFutureWeek: boolean;
  currentWeekDay: number;
}

@Component({
  selector: 'week-picker',
  templateUrl: './week-picker.component.html',
  styleUrls: ['./week-picker.component.scss']
})
export class WeekPickerComponent implements OnInit {
  model = '';

  @Output() weekSelect = new EventEmitter<{ start: NgbDate; end: NgbDate }>();
  @Output() weekDetails = new EventEmitter<IWeekDetails>();
  selectedWeek: { start: NgbDate; end: NgbDate } | null = null;

  constructor(private calendar: NgbCalendar) {}

  ngOnInit(): void {
    const todayDate = new Date();
    this.onDateSelection(new NgbDate(todayDate.getFullYear(), todayDate.getMonth() + 1, todayDate.getDate()));
  }

  // Get the start of the week for a given date
  private getStartOfWeek(date: NgbDate): NgbDate {
    const dayOfWeek = this.calendar.getWeekday(date);
    return this.calendar.getNext(date, 'd', -(dayOfWeek - 1)); // Adjust to Monday as the start
  }

  // Get the end of the week for a given date
  private getEndOfWeek(date: NgbDate): NgbDate {
    const dayOfWeek = this.calendar.getWeekday(date);
    return this.calendar.getNext(date, 'd', 6 - dayOfWeek); // Adjust to Sunday as the end
  }

  onDateSelection(date: NgbDate) {
    const startOfWeek = this.getStartOfWeek(date);
    const endOfWeek = this.getEndOfWeek(date);
    const weekDetails: IWeekDetails = {
      currentWeekDay: 0,
      isCurrentWeek: false,
      isFutureWeek: false,
      isPastWeek: false
    };
    this.selectedWeek = { start: startOfWeek, end: endOfWeek };
    this.weekSelect.emit(this.selectedWeek);
    weekDetails.isCurrentWeek = this.isNotCurrentWeek(date);

    weekDetails.isFutureWeek = this.isNgbDateFutureWeek(date);
    weekDetails.isPastWeek = this.isNgbDatePastWeek(date);
    weekDetails.currentWeekDay = new Date(date.year, date.month - 1, date.day).getDay();

    this.weekDetails.emit(weekDetails);
  }

  // Mark dates outside the selected week as disabled
  isNotCurrentWeek(date: NgbDate): boolean {
    if (!this.selectedWeek) {
      return false; // No week is selected
    }

    const inputDate = new Date(date.year, date.month - 1, date.day);
    const today = new Date();

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    // Get the end of the current week (Saturday)
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()));

    // Reset time components to compare only dates
    startOfWeek.setHours(0, 0, 0, 0);
    endOfWeek.setHours(23, 59, 59, 999);

    return inputDate >= startOfWeek && inputDate <= endOfWeek;
  }

  private isNgbDatePastWeek(date: NgbDate): boolean {
    const today = NgbDate.from({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate()
    });
    if (today) {
      return this.getWeekNumber(date) < this.getWeekNumber(today);
    } else {
      return false;
    }
  }

  private isNgbDateFutureWeek(date: NgbDate): boolean {
    const today = NgbDate.from({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate()
    });
    if (today) {
      return this.getWeekNumber(date) > this.getWeekNumber(today);
    } else {
      return false;
    }
  }

  private getWeekNumber(date: NgbDate): number {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    const firstDayOfYear = new Date(jsDate.getFullYear(), 0, 1);
    const pastDaysOfYear = (jsDate.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }
}
